import type { LANG } from '@eshop/type'
import type Eshop from '@eshop/type/eshop'

import i18n from './i18n'
import css from './index.module.css'

import { Svg } from '#/global/component/Image/Svg'
import { Tooltip } from '#/global/component/Tooltip'

import { ProductHelper } from '#/global/helper/Product/ProductHelper'
import { ProductPriceHelper } from '#/global/helper/Product/ProductPriceHelper'

interface Props {
    lang: LANG
    product: Eshop.Product<'price' | 'unit' | 'detail'>
}

export const PriceEshopPreview = ({ lang, product }: Props) => {
    const { price, unit } = product

    const format = ProductHelper.formatPrice
    const currency = price.currencyCode

    const t = i18n[lang]

    const primaryPrice = format(price.priceVatPrimary, currency).split(',')
    const secondaryPrice = format(price.priceVatSecondary, currency).split(',')

    const tax = price.hasTax ? 'DPH a PHE' : 'DPH'

    const tooltip = () => {
        if (price.isB2B || !ProductPriceHelper.hasDiscountPromo(product)) {
            return t.tooltipB2B(
                format(price.priceVatPrimaryOrigin, currency),
                format(price.priceNoVatPrimaryOrigin, currency),
                tax,
            )
        }
        return t.tooltipLowest(format(price.priceVatLowest30days, currency), tax)
    }

    return (
        <div className={`r-product-price-eshop ${css.wrap}`}>

            {/* Has secondary price */}
            {price.isPackagePrimary && (
                <>
                    {/* Secondary price */}
                    <div className={css.priceSecondary}>
                        {secondaryPrice[0]}
                        <span>
                            ,
                            {secondaryPrice[1]}
                        </span>
                    </div>
                    {/* Unit */}
                    <div className={css.pricePerUnit}>{t.pricePerUnit(unit.unitSales)}</div>
                </>
            )}

            {/* Origin price */}
            {ProductPriceHelper.hasDiscount(product) && (
                <div className={css.discount}>
                    <div className={css.strike}>
                        {price.isB2B || !ProductPriceHelper.hasDiscountPromo(product)
                            ? format(price.priceVatPrimaryOrigin, currency)
                            : format(price.priceVatLowest30days, currency)}
                    </div>
                    <Tooltip text={tooltip()}>
                        <Svg className={css.tooltipImage} src="/file/icon-info-circled-6d8bc63d.svg" width="16" height="16" />
                    </Tooltip>
                </div>
            )}

            {/* Primary price */}
            <div className={css.pricePrimary}>
                {primaryPrice[0]}
                <span>
                    ,
                    {primaryPrice[1]}
                </span>
            </div>
            {/* Unit */}
            <div className={css.pricePerUnit}>{t.pricePerUnit(unit.unitPrimary)}</div>
        </div>
    )
}
